import { ICommonSettings, ITrelloMcpSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigTrelloMcpSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: ITrelloMcpSetting;
}

export interface IConfigTrelloMcpSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingTrelloMcpId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigTrelloMcpSettings extends React.PureComponent<
  IConfigTrelloMcpSettingsProps,
  IConfigTrelloMcpSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigTrelloMcpSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigTrelloMcpSettingsProps & IWithSDProps) {
    if (prevProps.editingTrelloMcpId !== this.props.editingTrelloMcpId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (!this.props.editingTrelloMcpId) return;

    const editingConfig = this.props.commonSettings.trelloMcpSettings?.find(
      (x) => x.id === this.props.editingTrelloMcpId
    );

    if (!editingConfig) return;
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const trelloMcpSetting = this.props.commonSettings?.trelloMcpSettings;
    if (!trelloMcpSetting) return;

    const isSettingAlreadyExist = trelloMcpSetting.some(
      (x) => x.name === value && x.id !== this.props.editingTrelloMcpId
    );

    if (isSettingAlreadyExist) throw new Error("Trello Mcp Name. is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: ITrelloMcpSetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      apiKey: data.apiKey,
      token: data.token
    };

    if (this.props.editingTrelloMcpId) {
      obj.id = this.props.editingTrelloMcpId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "trelloMcpSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "trelloMcpSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingTrelloMcpId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingTrelloMcpId || "",
      configKey: "trelloMcpSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Trello Mcp setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Trello Mcp setting"
        secondHeader="Trello Mcp setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingTrelloMcpId ? this.deleteSetting : undefined}
            deleteTestId="mcpTrelloSettingDeleteButton"
            submitTestId="mcpTrelloSettingCreateButton"
            submitLabel={this.props.editingTrelloMcpId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configTrelloForm"
          />
        }
      >
        <Form
          id="configTrelloForm"
          name="trelloMcpSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription("mcp_trellomcp_description")}>
            <ConfigField label="Name" required tooltipContentKey="mcp_trellomcp_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Trello Mcp Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Trello Mcp Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="mcpTrelloSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="API Key" required tooltipContentKey="mcp_trellomcp_apikey">
              <Form.Item name="apiKey" rules={[{ required: true, message: "Please enter a Trello Mcp API Key." }]}>
                <KuikaInput size="md" data-testid="mcpTrelloSettingApiKeyInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Token" required tooltipContentKey="mcp_trellomcp_token">
              <Form.Item name="token" rules={[{ required: true, message: "Please enter a Trello Mcp Token." }]}>
                <KuikaInput size="md" data-testid="mcpTrelloSettingTokenInput" autoFocus />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
